import { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import {
    add,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    sub,
} from 'date-fns';
import { isPlainObject } from 'lodash';

import { GrowthAllocationListColumns } from '../../Allocation/AllocationListColumns';
import { usePreTradeAllocationReport } from '../../api/allocation';
import { useAssets } from '../../api/asset';
import { usePortfolios } from '../../api/portfolio';
import { usePositions } from '../../api/position';
import { useWithdrawals } from '../../api/withdrawal';
import { assetListColumns } from '../../Asset/assetListColumns';
import { portfolioListColumns } from '../../Portfolio/portfolioListColumns';
import { positionListColumns } from '../../Position/positionListColumns';
import { formatDate, SERVER_DATE_FORMAT, today } from '../../utils/dateUtils';
import { withdrawalListColumns } from '../../Withdrawal/withdrawalListColumns';

export const getPageUrl = (type: DashboardWidgetType) => {
    switch (type) {
        case 'portfolio':
            return '/portfolios';
        case 'position':
            return '/positions';
        case 'securityMaster':
            return '/security-master';
        case 'withdrawal':
            return '/withdrawals';
        case 'growthAllocation':
            return '/allocation';
        default:
            throw new Error('Unknown widget type');
    }
};

export const getQueryFn = (type: DashboardWidgetType) => {
    switch (type) {
        case 'portfolio':
            return usePortfolios;
        case 'position':
            return usePositions;
        case 'securityMaster':
            return useAssets;
        case 'withdrawal':
            return useWithdrawals;
        case 'growthAllocation':
            return usePreTradeAllocationReport;
        default:
            throw new Error('Unknown widget type');
    }
};

export const getColumns = (type: DashboardWidgetType) => {
    switch (type) {
        case 'portfolio': {
            return portfolioListColumns;
        }
        case 'position': {
            return positionListColumns;
        }
        case 'securityMaster': {
            return assetListColumns;
        }
        case 'withdrawal': {
            return withdrawalListColumns;
        }
        case 'growthAllocation': {
            return GrowthAllocationListColumns;
        }
        default:
            throw new Error('Unknown widget type');
    }
};

export const getColumnDefinitions = (type: DashboardWidgetType, columnIds: string[]): ColumnDef<any>[] => {
    const cols = getColumns(type);
    return columnIds.map((id) => cols.find((col) => col.id === id)).filter(Boolean) as ColumnDef<any>[];
};

export const parseFilter = (filters: ColumnFiltersState) => {
    return filters.map((filter) => {
        if (isPlainObject(filter.value) && Object.prototype.hasOwnProperty.call(filter.value, 'operator')) {
            return {
                id: filter.id,
                value: operatorToFilterValue(filter.value as any),
            };
        }
        return filter;
    });
};

export const operatorToFilterValue = ({
    operator,
    amount,
    unit,
}: {
    operator: string;
    amount?: number;
    unit?: string;
}) => {
    const t = today();
    switch (operator) {
        case 'week': {
            return {
                from: formatDate(startOfWeek(t), SERVER_DATE_FORMAT),
                to: formatDate(endOfWeek(t), SERVER_DATE_FORMAT),
            };
        }
        case 'month': {
            return {
                from: formatDate(startOfMonth(t), SERVER_DATE_FORMAT),
                to: formatDate(endOfMonth(t), SERVER_DATE_FORMAT),
            };
        }
        case 'quarter': {
            return {
                from: formatDate(startOfQuarter(t), SERVER_DATE_FORMAT),
                to: formatDate(endOfQuarter(t), SERVER_DATE_FORMAT),
            };
        }
        case 'year': {
            return {
                from: formatDate(startOfYear(t), SERVER_DATE_FORMAT),
                to: formatDate(endOfYear(t), SERVER_DATE_FORMAT),
            };
        }
        case 'next': {
            if (!unit) {
                return {};
            }
            return {
                from: formatDate(t, SERVER_DATE_FORMAT),
                to: formatDate(add(t, { [unit]: amount }), SERVER_DATE_FORMAT),
            };
        }
        case 'last': {
            if (!unit) {
                return {};
            }
            return {
                from: formatDate(sub(t, { [unit]: amount }), SERVER_DATE_FORMAT),
                to: formatDate(t, SERVER_DATE_FORMAT),
            };
        }
    }
};
